import { Injectable, Inject } from '@angular/core'
import { Effect, Actions, ofType } from '@ngrx/effects'
import { of } from 'rxjs/observable/of'
import { Store } from '@ngrx/store'
import { catchError, debounceTime, exhaustMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { IScheduler } from 'rxjs/Scheduler'
import { async } from 'rxjs/scheduler/async'
import { environment } from '../../../../../environments/environment'
import { LoginTokenModel } from '../../../../api/identity/models/login-token-model'

import * as App from '../../../../store/actions/app.actions'
import * as Auth from '../actions/auth.actions'
import * as User from '../actions/user.actions'
import * as userSelectors from '../selectors/user.selectors'
import * as authSelectors from '../selectors/auth.selectors'
import { AppState } from '../../../../store'

import { SignalRService } from '../../../signalR'
import { errorActions } from '../../../utils/helpers'

import { JeanService } from '../../../../api/api/services/jean.service'
import { UserV2Service } from '../../../../api/identity/services/user-v2.service'

@Injectable()
export class AuthEffects {

  constructor(private actions$: Actions,
              private authService: UserV2Service,
              private jeanService: JeanService,
              private store: Store<AppState>,
              private signalRService: SignalRService,
              @Inject('DEBOUNCE_TIME') private delayTime: number = environment.debounceTime,
              @Inject('SCHEDULER') private scheduler: IScheduler = async) {
  }
  /*
   LOGIN
    */
  @Effect({dispatch: false})
  login$ = this.actions$.pipe(
    ofType(Auth.LOGIN),
    debounceTime(this.delayTime, this.scheduler),
    tap(() => this.authService.Login())
  )

  @Effect({dispatch: false})
  signInSilentCallback$ = this.actions$.pipe(
    ofType(Auth.SIGNIN_SILENT_CALLBACK),
    map((action: Auth.SignInSilentCallback) => action.payload),
    tap(({url}) => this.authService.SignInSilentCallback(url))
  )

  @Effect()
  signinRedirect$ = this.actions$.pipe(
    ofType(Auth.SIGNIN_REDIRECT),
    map((action: Auth.SignInRedirect) => action.payload),
    exhaustMap(({url}) =>
      this.authService.SigninRedirectCallback(url).pipe(
        map((loginToken) => new Auth.LoginSuccess(loginToken)),
        catchError( error => of(new Auth.LoginFailure(error)))
      )
    ))

  @Effect()
  loginSuccess$ = this.actions$.pipe(
    ofType(Auth.LOGIN_SUCCESS),
    exhaustMap(_ =>
      this.jeanService
        .GetLoggedVendorUser({version: '1'}).pipe(
          mergeMap(user => [
            new User.Me({user}),
            new Auth.LoggedInRedirect()
          ]),
          catchError(err => of(new App.ApiCallFailure({
            title: 'Error',
            msg: 'Could not load user info.'
        }))))
    ))

  @Effect()
  loginFailure$ = this.actions$.pipe(
    ofType(Auth.LOGIN_FAILURE),
    map((action: Auth.LoginFailure) => action.payload),
    map(error => new App.ApiCallFailure({
      title: 'Error',
      msg: 'Could not login: \n' + error
    })))

  /*
  already logged in
   */
  @Effect()
  loggedInRedirect$ = this.actions$.pipe(
    ofType(Auth.LOGGED_IN_REDIRECT),
    switchMap(_ => of(new App.Go({path: ['/']}))))

  @Effect()
  meRefresh$ = this.actions$.pipe(
    ofType(User.ME_REFRESH),
    switchMap(_ =>
      this.jeanService
        .GetLoggedVendorUser({version: '1'}).pipe(
        map(user => new User.Me({user})),
        catchError(err => of(new App.ApiCallFailure({
          title: 'Error',
          msg: 'Could not load user info.' + err
        }))))
    ))
  /*
   first time generated password, need to change it
    */
  @Effect()
  firstTimeLoginRedirect$ = this.actions$.pipe(
    ofType(Auth.FIRST_TIME_LOGIN_REDIRECT),
    map((action: Auth.FirstTimeLoginRedirect) => action.payload),
    map(({email}) => new App.Go({path: ['/login'], query: {email}}))) // FIXME when idsrv done

  @Effect()
  refreshToken$ = this.actions$.pipe(
    ofType(Auth.REFRESH_TOKEN),
    withLatestFrom(this.store.select(userSelectors.getUserState)),
    withLatestFrom(this.store.select(authSelectors.getAuthState)),
    map(([[action, userState], authState]) => ({authState, userState})),
    exhaustMap(({authState, userState}) =>
      this.authService
        .RefreshToken().pipe(
          map((res: LoginTokenModel) => new Auth.RefreshTokenSuccess(res)),
          catchError(err => {
            this.signalRService.stopAutoRefreshingToken()
            return errorActions(
              new Auth.Logout({email: userState.user.email}),
              new App.ApiCallFailure({
                title: 'Error',
                msg: 'Could not refresh token, please log in.'
          })
        )}))
    ))

  /*
   LOGOUT
    */
  @Effect()
  logout$ = this.actions$.pipe(
    ofType(Auth.LOGIN_REDIRECT, Auth.LOGOUT),
    withLatestFrom(this.store.select(authSelectors.getAuthLoggedIn)),
    // proceed only if still logged in, prevents multiple redirects from api calls
    filter(([action, loggedIn]) => loggedIn || action.type === Auth.LOGIN_REDIRECT),
    map(([action, _]) => {
        this.signalRService.stopAutoRefreshingToken()
        let query = {}
        if (action instanceof Auth.Logout && action.payload.email) {
          query = {email: action.payload.email}
        }
        return new Auth.LogoutCommon({query})
    }))

  @Effect({dispatch: false})
  logoutApi$ = this.actions$.pipe(
    ofType(Auth.LOGOUT_API),
    tap(action => {
      this.signalRService.stopAutoRefreshingToken()
      this.authService.Logout()
    }))

  @Effect()
  logoutCommon$ = this.actions$.pipe(
    ofType(Auth.LOGOUT_COMMON),
    map((action: Auth.LogoutCommon) => action.payload.query),
    mergeMap(query => {
      this.signalRService.stopAutoRefreshingToken()
      return [
        new Auth.LogoutSuccess(),
        new App.Go({path: ['/login'], query})
    ]}),
    tap(async _ => {console.log('LOGOUT'); return await this.signalRService.stopConnection()})
  )
}



// WEBPACK FOOTER //
// ./src/app/shared/auth/store/effects/auth.effects.ts